<template>
  <div id="voice_agent_campaign_create_edit">
    <div
      ref="scrollContainerRef"
      class="flex flex-col gap-6 overflow-y-scroll"
      :class="{ 'px-[24vw] py-6': !isEdit }"
      style="max-height: calc(100vh - (128px + var(--banner-height)))"
    >
      <SkeletonLoader
        v-if="isLoading"
        :page="
          isEdit
            ? loaderPages.VOICE_AGENT_CAMPAIGN_EDIT
            : loaderPages.VOICE_AGENT_CAMPAIGN_CREATE
        "
      />

      <div
        v-else
        class="flex flex-col gap-6 proxima_nova secondary_color text-[13px]"
      >
        <CampaignFieldRenderer
          v-for="field in filteredFields"
          :key="field.type"
          :field-type="field.type"
          :model-value="
            field.modelKey ? campaignFormData[field.modelKey] : undefined
          "
          @update:model-value="
            field.modelKey ? (campaignFormData[field.modelKey] = $event) : null
          "
          @update:timezone-data="
            field.type === campaignFieldTypes.TIMEZONE_SETTINGS
              ? (timezoneData = $event)
              : null
          "
          @update:time-error="timeError = $event"
          :scroll-container-ref="scrollContainerRef"
          v-bind="getFieldProps(field)"
          v-on="getFieldEvents(field)"
        />

        <MapVariablesToColumnsModal
          v-if="showMapVariablesModal"
          :visible="showMapVariablesModal"
          :variables="dynamicVariables"
          :campaign-id="campaignId"
          :voice-agent-id="campaignFormData.voiceAgentId"
          :saved-mapping="variableMapping"
          @update:visible="showMapVariablesModal = $event"
          @mapping-saved="handleMappingSaved"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";
import { debounce } from "lodash";
import { toGetPhoneNumbers } from "@/src/utils/callsUtils.js";
import {
  campaignFieldConfigurations,
  defaultActiveDays,
  campaignFieldTypes,
} from "@/src/constants/callCampaignConstants.js";
import {
  fetchVoiceAgents,
  formatAgentName,
  fetchDynamicVariablesForAgent,
} from "@/src/utils/voiceAgentUtils.js";
import { telephonyProviders } from "@/src/constants/telephonyConstants.js";
import { dialerTypes, consentTimeouts } from "@/src/constants/callConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import CallService from "@/src/api/CallService.js";
import VoiceAgentService from "@/src/api/VoiceAgentService.js";
import CampaignFieldRenderer from "@/src/components/campaigns/CampaignFieldRenderer.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import MapVariablesToColumnsModal from "@/src/components/voice_agents/MapVariablesToColumnsModal.vue";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import dayjs from "dayjs";

const router = useRouter();

const props = defineProps({
  campaignName: {
    type: String,
    default: "Untitled Campaign",
  },
  source: {
    type: String,
    default: "create_voice_agent_campaign",
  },
  entityListId: {
    type: String,
    default: null,
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
  campaignData: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits([
  "update:campaign-name",
  "update:formData",
  "update:isValid",
]);

const isLoading = ref(false);
const scrollContainerRef = ref(null);
const timeError = ref("");
const campaignFormData = ref({
  name: props.campaignName,
  voiceAgentId: null,
  phoneNumberId: null,
  parallelCalls: 2,
  cooldownDuration: 0,
  isRecordingEnabled: false,
  entityListId: props.entityListId,
  dncFollow: true,
  useLocalTimeZone: false,
  dayAllowed: defaultActiveDays,
  startTime: "9:00am",
  endTime: "5:00pm",
});

const voiceAgents = ref([]);
const phoneNumbers = ref([]);
const creatingCampaign = ref(false);
const voiceAgentSearchQuery = ref("");
const dynamicVariables = ref([]);
const showMapVariablesModal = ref(false);
const campaignId = ref(null);
const variableMapping = ref({});

let consentFlowCleanup = null;

const voiceAgentOptions = computed(() => {
  try {
    if (!voiceAgents.value || !Array.isArray(voiceAgents.value)) {
      return [];
    }
    return voiceAgents.value.map((agent) => ({
      id: agent.id,
      name: formatAgentName(agent.agent_name || agent.name),
      voice: agent.voice,
    }));
  } catch (error) {
    console.error("Error processing voice agent options:", error);
    return [];
  }
});

const isGlobalRecordingEnabled = computed(
  () => store.getters["dialerSettings/isGlobalRecordingEnabled"]
);

const voiceAgentConsentStatus = computed(
  () => store.getters["voiceAgents/voiceAgentConsentStatus"]
);

const isFormValid = computed(() => {
  try {
    const baseValidation =
      campaignFormData.value.name &&
      campaignFormData.value.voiceAgentId &&
      campaignFormData.value.phoneNumberId &&
      !timeError.value;

    if (props.isEdit) {
      return baseValidation;
    }

    return baseValidation && campaignFormData.value.entityListId;
  } catch (error) {
    console.error("Error validating form:", error);
    return false;
  }
});

const showMapVariablesField = computed(() => {
  try {
    return (
      campaignFormData.value.voiceAgentId &&
      campaignFormData.value.entityListId &&
      dynamicVariables.value.length > 0 &&
      !props.isEdit
    );
  } catch (error) {
    console.error("Error computing showMapVariablesField:", error);
    return false;
  }
});

const filteredFields = computed(() => {
  try {
    if (!campaignFieldConfigurations?.voice_agent_campaign) return [];

    const config = campaignFieldConfigurations.voice_agent_campaign;
    return config.filter((field) => {
      if (!field) return false;

      if (field.type === campaignFieldTypes.MAP_VARIABLES) {
        return showMapVariablesField.value;
      }

      if (!field.condition) return true;

      if (field.condition === "isCreate") {
        return !props.isEdit;
      }

      return true;
    });
  } catch (error) {
    console.error("Error filtering fields:", error);
    return [];
  }
});

const timezoneData = computed({
  get: () => ({
    useLocalTimeZone: campaignFormData.value.useLocalTimeZone,
    dayAllowed: campaignFormData.value.dayAllowed,
    startTime: campaignFormData.value.startTime,
    endTime: campaignFormData.value.endTime,
  }),
  set: (value) => {
    campaignFormData.value.useLocalTimeZone = value.useLocalTimeZone;
    campaignFormData.value.dayAllowed = value.dayAllowed;
    campaignFormData.value.startTime = value.startTime;
    campaignFormData.value.endTime = value.endTime;
  },
});

const fieldDataMap = computed(() => {
  try {
    return {
      voiceAgents: voiceAgentOptions.value || [],
      phoneNumbers: phoneNumbers.value || [],
      formData: campaignFormData.value || {},
      actionButtonsData: {
        loading: creatingCampaign.value,
        disabled: !isFormValid.value,
      },
      voiceAgentSearch: debouncedSearchVoiceAgents,
      timezoneData: timezoneData.value,
    };
  } catch (error) {
    console.error("Error creating field data map:", error);
    return {};
  }
});

const getFieldProps = (field) => {
  try {
    if (!field) return {};

    const props = { ...(field.staticProps || {}) };

    if (field.dataKey && fieldDataMap.value[field.dataKey]) {
      const data = fieldDataMap.value[field.dataKey];

      if (field.propName) {
        props[field.propName] = data;
      } else if (field.type === "actionButtons") {
        props.loading = data.loading;
        props.disabled = data.disabled;
      }
    }

    if (field.type === "voiceAgent" && fieldDataMap.value.voiceAgentSearch) {
      props.onSearch = fieldDataMap.value.voiceAgentSearch;
    }

    if (field.type === "timezoneSettings" && fieldDataMap.value.timezoneData) {
      props.timezoneData = fieldDataMap.value.timezoneData;
    }

    return props;
  } catch (error) {
    console.error("Error getting field props:", error);
    return {};
  }
};

const getFieldEvents = (field) => {
  try {
    if (!field?.events || !Array.isArray(field.events)) return {};

    const events = {};

    if (field.events.includes("cancel")) {
      events.cancel = toCancelCreateCampaign;
    }
    if (field.events.includes("create")) {
      events.create = toCreateVoiceAgentCampaign;
    }
    if (field.events.includes("openModal")) {
      events.openModal = () => {
        showMapVariablesModal.value = true;
      };
    }

    return events;
  } catch (error) {
    console.error("Error getting field events:", error);
    return {};
  }
};

const getVoiceAgents = async (searchQuery = "") => {
  try {
    const agents = await fetchVoiceAgents({
      pageSize: 20,
      searchQuery,
    });
    voiceAgents.value = agents || [];
  } catch (error) {
    console.error("Error fetching voice agents:", error);
    message.error("An error occurred while fetching voice agents");
  }
};

const searchVoiceAgents = async (searchTerm = "") => {
  try {
    voiceAgentSearchQuery.value = searchTerm;
    await getVoiceAgents(searchTerm);
  } catch (error) {
    console.error("Error searching voice agents:", error);
  }
};

const debouncedSearchVoiceAgents = debounce(searchVoiceAgents, 300);

const fetchPhoneNumbers = async () => {
  try {
    const numbers = await toGetPhoneNumbers({ user: true });
    phoneNumbers.value = (numbers || []).filter(
      (num) =>
        num.provider === telephonyProviders.plivo ||
        num.provider === telephonyProviders.twilio
    );
  } catch (error) {
    console.error("Error fetching phone numbers:", error);
    message.error("An error occurred while fetching phone numbers");
  }
};


const toCancelCreateCampaign = () => {
  try {
    router.push({ name: "voice-agents-campaign" });
  } catch (error) {
    console.error("Error navigating to campaigns:", error);
  }
};

const handleGetData = async () => {
  try {
    isLoading.value = true;

    await Promise.all([
      getVoiceAgents(),
      fetchPhoneNumbers(),
    ]);
  } catch (error) {
    console.error("An error occurred while handling get data:", error);
    message.error("An error occurred while loading campaign data");
  } finally {
    isLoading.value = false;
  }
};

const fetchDynamicVariables = async (agentId) => {
  try {
    const variables = await fetchDynamicVariablesForAgent(agentId);
    dynamicVariables.value = variables || [];
  } catch (error) {
    console.error("Error fetching dynamic variables:", error);
    dynamicVariables.value = [];
  }
};

const handleVoiceAgentChange = async (newAgentId, oldAgentId) => {
  try {
    if (newAgentId) {
      await fetchDynamicVariables(newAgentId);
      
      if (oldAgentId !== newAgentId) {
        variableMapping.value = {};
      }
    } else {
      dynamicVariables.value = [];
      variableMapping.value = {};
    }
  } catch (error) {
    console.error("Error handling voice agent change:", error);
  }
};


const saveMappingToCampaign = async (createdCampaignId) => {
  try {
    if (
      !variableMapping.value ||
      Object.keys(variableMapping.value).length === 0
    ) {
      return;
    }

    const payload = {
      campaign_id: createdCampaignId,
      voice_agent_id: campaignFormData.value.voiceAgentId,
      dynamic_variables: variableMapping.value,
    };

    const response = await VoiceAgentService.createCampaignMapping(payload);

    if (response?.success) {
      message.success("Variable mapping saved successfully!");
    }
  } catch (error) {
    console.error("Error saving variable mapping:", error);
  }
};

const proceedWithCampaignCreation = async () => {
  try {
    if (!isFormValid.value) {
      return;
    }

    creatingCampaign.value = true;

    const payload = {
      name: campaignFormData.value.name,
      entity_list_id: campaignFormData.value.entityListId,
      phone_number_id: campaignFormData.value.phoneNumberId,
      agent_id: campaignFormData.value.voiceAgentId,
      record_calls: campaignFormData.value.isRecordingEnabled,
      cooldown_time: campaignFormData.value.cooldownDuration,
      campaign_type: dialerTypes.VOICE_AGENTS_DIALER,
      parallel_calls: campaignFormData.value.parallelCalls,
      dnc_follow: campaignFormData.value.dncFollow,
    };

    if (campaignFormData.value.useLocalTimeZone) {
      payload.day_allowed = campaignFormData.value.dayAllowed;
      if (campaignFormData.value.startTime) {
        payload.start_time = dayjs(campaignFormData.value.startTime, [
          "h:mma",
          "h:mm a",
        ]).format("HH:mm");
      }
      if (campaignFormData.value.endTime) {
        payload.end_time = dayjs(campaignFormData.value.endTime, [
          "h:mma",
          "h:mm a",
        ]).format("HH:mm");
      }
    }

    const response = await CallService.createCallCampaign(payload);

    if (response?.success) {
      campaignId.value = response?.campaign?.id;
      message.success("Voice agent campaign created successfully");

      await saveMappingToCampaign(campaignId.value);

      router.push({
        name: "voice-agents-campaign-view",
        params: { id: campaignId.value },
      });
    }
  } catch (error) {
    console.error("Error creating voice agent campaign:", error);
    message.error("An error occurred while creating the campaign");
  } finally {
    creatingCampaign.value = false;
  }
};

const handleMappingSaved = (mapping) => {
  try {
    variableMapping.value = { ...mapping };
    showMapVariablesModal.value = false;
  } catch (error) {
    console.error("Error handling mapping saved:", error);
  }
};

const handleConsentFlow = async () => {
  let unwatchFn = null;
  let timeoutId = null;

  const cleanup = () => {
    if (unwatchFn) {
      unwatchFn();
      unwatchFn = null;
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    eventBus.off("voice-agent-consent-modal-closed", cleanup);
    consentFlowCleanup = null;
  };

  const handleConsentUpdate = async (updatedConsent) => {
    if (updatedConsent) {
      cleanup();
      await proceedWithCampaignCreation();
    }
  };

  eventBus.emit("open-voice-agent-consent-form", true);

  unwatchFn = store.watch(
    () => store.getters["voiceAgents/voiceAgentConsentStatus"],
    handleConsentUpdate
  );

  eventBus.on("voice-agent-consent-modal-closed", cleanup);
  timeoutId = setTimeout(cleanup, consentTimeouts.VOICE_AGENT_CONSENT);

  consentFlowCleanup = cleanup;
};

const toCreateVoiceAgentCampaign = async () => {
  try {
    if (!isFormValid.value) {
      return;
    }

    if (voiceAgentConsentStatus.value) {
      await proceedWithCampaignCreation();
    } else {
      await handleConsentFlow();
    }
  } catch (error) {
    console.error("Error in toCreateVoiceAgentCampaign:", error);
    message.error("An error occurred while creating the campaign");
  }
};

const handleCampaignNameChange = (newName) => {
  try {
    campaignFormData.value.name = newName;
  } catch (error) {
    console.error("Error handling campaign name change:", error);
  }
};

const handleFormNameChange = (newName) => {
  try {
    emit("update:campaign-name", newName);
  } catch (error) {
    console.error("Error handling form name change:", error);
  }
};

const populateFormDataFromCampaign = (campaignData) => {
  try {
    if (!props.isEdit || !campaignData) return;

    const useLocalTimeZone = !!(
      campaignData.day_allowed && campaignData.day_allowed.length > 0
    );

    campaignFormData.value = {
      ...campaignFormData.value,
      name: campaignData.name || campaignFormData.value.name,
      voiceAgentId:
        campaignData.agent_id || campaignFormData.value.voiceAgentId,
      phoneNumberId:
        campaignData.phone_number_id || campaignFormData.value.phoneNumberId,
      parallelCalls:
        campaignData.parallel_calls || campaignFormData.value.parallelCalls,
      cooldownDuration:
        campaignData.cooldown_time !== undefined
          ? campaignData.cooldown_time
          : campaignFormData.value.cooldownDuration,
      entityListId:
        campaignData.entity_list_id || campaignFormData.value.entityListId,
      dncFollow:
        campaignData.dnc_follow !== undefined ? campaignData.dnc_follow : true,
      useLocalTimeZone: useLocalTimeZone,
      dayAllowed: campaignData.day_allowed || defaultActiveDays,
      startTime: campaignData.start_time
        ? dayjs(campaignData.start_time, "HH:mm").format("h:mma")
        : "9:00am",
      endTime: campaignData.end_time
        ? dayjs(campaignData.end_time, "HH:mm").format("h:mma")
        : "5:00pm",
    };
  } catch (error) {
    console.error("Error populating form data from campaign:", error);
  }
};

watch(() => props.campaignName, handleCampaignNameChange);

watch(() => campaignFormData.value.name, handleFormNameChange);

watch(
  campaignFormData,
  (newVal) => {
    if (props.isEdit) {
      emit("update:formData", newVal);
    }
  },
  { deep: true }
);

watch(
  isFormValid,
  (newVal) => {
    if (props.isEdit) {
      emit("update:isValid", newVal);
    }
  },
  { immediate: true }
);

watch(() => props.campaignData, populateFormDataFromCampaign, {
  immediate: true,
  deep: true,
});

watch(
  () => campaignFormData.value.voiceAgentId,
  (newAgentId, oldAgentId) => {
    handleVoiceAgentChange(newAgentId, oldAgentId);
  }
);

onMounted(async () => {
  await handleGetData();
  if (!props.isEdit) {
    campaignFormData.value.isRecordingEnabled = isGlobalRecordingEnabled.value;
  }
});

onUnmounted(() => {
  if (consentFlowCleanup) {
    consentFlowCleanup();
  }
});
</script>
